/**
 * TextArea Component
 *
 * A flexible textarea component built with Community Trust design tokens.
 * Supports labels, help text, error states, and character counting.
 */

import React from 'react';
import { clsx } from 'clsx';

export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /**
   * Label text for the textarea
   */
  label?: string;

  /**
   * Helper text displayed below the textarea
   */
  helperText?: string;

  /**
   * Error message (displays textarea in error state)
   */
  error?: string;

  /**
   * Success message (displays textarea in success state)
   */
  success?: string;

  /**
   * Whether the textarea should take full width
   */
  fullWidth?: boolean;

  /**
   * Show character count
   */
  showCount?: boolean;

  /**
   * Maximum character length (also enforces maxLength)
   */
  maxLength?: number;
}

/**
 * TextArea component using Community Trust design system
 */
export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      helperText,
      error,
      success,
      fullWidth = false,
      showCount = false,
      maxLength,
      className,
      disabled,
      value,
      ...props
    },
    ref
  ) => {
    const textareaId = React.useId();
    const [charCount, setCharCount] = React.useState(0);

    // Track character count
    React.useEffect(() => {
      if (value !== undefined) {
        setCharCount(String(value).length);
      }
    }, [value]);

    // Container styles
    const containerStyles = clsx({
      'w-full': fullWidth,
      'inline-block': !fullWidth,
    });

    // Base textarea styles
    const baseTextareaStyles = clsx(
      'border border-neutral-300 rounded-lg',
      'px-4 py-2 text-base',
      'transition-all duration-base',
      'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent',
      'disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-neutral-100',
      'placeholder:text-neutral-400',
      'resize-y', // Allow vertical resizing only
      {
        'w-full': fullWidth,
      }
    );

    // State-specific styles
    const stateStyles = clsx({
      'border-error-500 focus:ring-error-500': error,
      'border-success-500 focus:ring-success-500': success && !error,
    });

    // Character count color
    const countColor = maxLength && charCount > maxLength * 0.9
      ? 'text-warning-600'
      : 'text-neutral-500';

    return (
      <div className={containerStyles}>
        {/* Label and character count header */}
        <div className="flex items-center justify-between mb-1">
          {label && (
            <label
              htmlFor={textareaId}
              className="text-sm font-medium text-neutral-700"
            >
              {label}
            </label>
          )}

          {showCount && maxLength && (
            <span className={clsx('text-xs font-mono', countColor)}>
              {charCount}/{maxLength}
            </span>
          )}
        </div>

        {/* TextArea */}
        <textarea
          ref={ref}
          id={textareaId}
          disabled={disabled}
          maxLength={maxLength}
          value={value}
          className={clsx(
            baseTextareaStyles,
            stateStyles,
            className
          )}
          {...props}
        />

        {/* Helper/Error/Success text */}
        {(helperText || error || success) && (
          <p
            className={clsx(
              'mt-1 text-sm',
              {
                'text-neutral-600': !error && !success,
                'text-error-600': error,
                'text-success-600': success && !error,
              }
            )}
          >
            {error || success || helperText}
          </p>
        )}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export default TextArea;
